import { MessageBar, MessageBarBody, Spinner, makeStyles, tokens } from '@fluentui/react-components';
import { useQuery } from '@tanstack/react-query';
import { fetchFinancePL, fetchCashflow, fetchBudgetBreakdown } from '../api/dashboard.api';
import { fetchLotes } from '../api/lotes.api';
import { fetchAgingReport, AGING_BUCKETS } from '../api/sales.api';
import { KpiCard } from '../components/shared/KpiCard';
import { ChartCard } from '../components/shared/ChartCard';
import { DataTable, type DataTableColumn } from '../components/shared/DataTable';
import { DonutChart, type DonutChartSlice } from '../components/shared/charts/DonutChart';
import { BarLineChart } from '../components/shared/charts/BarLineChart';
import { useT } from '../i18n';
import { useLocaleSettings } from '../hooks/useLocaleSettings';
import type { BudgetBreakdownRow } from '../types/api.types';
import { useRanchoStore } from '../store/rancho.store';
import { PageHeader } from '../components/shared/PageHeader';

/**
 * Fase D — "Costos" del portal inversionista. Pantalla nueva (el mockup la
 * separa de "Inversión y Fondos"). Reusa 100% endpoints existentes de
 * finanzas (ADR-016: P&L, cashflow, budget breakdown) y de cartera (F3,
 * mismos que InvestorLogisticsPage) — sin backend nuevo.
 *
 * El mockup pide "cuentas por cobrar / pagar" pero el sistema solo modela
 * cuentas por COBRAR (no hay concepto de proveedores/AP hoy) — se muestra
 * solo AR, sin inventar la mitad de datos que no existen.
 */

const useStyles = makeStyles({
  root:    { display: 'flex', flexDirection: 'column', gap: tokens.spacingVerticalL, padding: tokens.spacingVerticalL },
  kpiRow:  { display: 'flex', gap: tokens.spacingHorizontalM, flexWrap: 'wrap' },
  splitRow: { display: 'flex', gap: tokens.spacingHorizontalL, flexWrap: 'wrap', alignItems: 'flex-start' },
  splitCol: { flex: '1', minWidth: '320px' },
  section: { display: 'flex', flexDirection: 'column', gap: tokens.spacingVerticalS },
  sectionTitle: { fontWeight: tokens.fontWeightSemibold, fontSize: tokens.fontSizeBase400 },
});

/** Paleta categórica rotativa — `costByCategory` trae claves libres por tenant,
 * no un enum fijo, así que no hay un color "correcto" por categoría de antemano. */
const CATEGORY_PALETTE = ['#1B7A32', '#0078d4', '#ca5010', '#8764b8', '#a4262c', '#0099bc', '#c19c00', '#498205'];

export function CostosPage() {
  const s = useStyles();
  const t = useT();
  const c = t.costos;
  const { formatCurrency } = useLocaleSettings();

  const activeRanchoId = useRanchoStore(st => st.activeRanchoId);
  const { data: pl, isLoading: plLoading } = useQuery({ queryKey: ['costos-pl', activeRanchoId], queryFn: () => fetchFinancePL({ ranchoId: activeRanchoId, from: '2000-01-01' }) });
  const { data: cashflow, isLoading: cashflowLoading } = useQuery({ queryKey: ['costos-cashflow', activeRanchoId], queryFn: () => fetchCashflow({ ranchoId: activeRanchoId }) });
  const { data: budget, isLoading: budgetLoading } = useQuery({ queryKey: ['costos-budget', activeRanchoId], queryFn: () => fetchBudgetBreakdown(undefined, activeRanchoId) });
  const { data: aging, isLoading: agingLoading } = useQuery({ queryKey: ['costos-aging', activeRanchoId], queryFn: () => fetchAgingReport(undefined, activeRanchoId) });

  const { data: lotes = [] } = useQuery({ queryKey: ['costos-lotes', activeRanchoId], queryFn: () => fetchLotes(activeRanchoId) });

  const fmt = (n: number) => formatCurrency(n);
  const execPct = budget && budget.totalPlanned > 0 ? (budget.totalExecuted / budget.totalPlanned) * 100 : null;

  // Las categorías llegan como clave en español (COSECHA, RIEGO...); se muestran en el idioma activo.
  const categoryName = (key: string) => {
    const k = key.toUpperCase();
    return (t.activityTypes as Record<string, string>)[k] ?? (k === 'INVENTARIO' ? t.nav.inventory : undefined) ?? key;
  };

  const categoryEntries = pl ? Object.entries(pl.costByCategory).filter(([, v]) => v > 0) : [];
  const categoryTotal = categoryEntries.reduce((sum, [, v]) => sum + v, 0);
  const donutData: DonutChartSlice[] = categoryEntries.map(([label, value], i) => ({
    label: categoryName(label),
    color: CATEGORY_PALETTE[i % CATEGORY_PALETTE.length],
    pct: categoryTotal > 0 ? Math.round((value / categoryTotal) * 1000) / 10 : 0,
  }));

  const balances = cashflow?.cashflow.map(p => p.balance) ?? [];
  const minBalance = Math.min(0, ...balances);
  const maxBalance = Math.max(1, ...balances);
  const range = maxBalance - minBalance || 1;
  const cashflowData = (cashflow?.cashflow ?? []).map(p => ({
    label: p.period,
    barHeightPct: ((p.balance - minBalance) / range) * 100,
  }));

  // "Indirectos prorrateados" (spec §9): línea aparte, en moneda base de gastos generales.
  const general = pl?.generalExpenses ?? null;
  const fmtBase = (n: number) => formatCurrency(n, general?.baseCurrency);
  const indirectRows = Object.entries(pl?.indirectsByLote ?? {})
    .filter(([, v]) => v > 0)
    .map(([loteId, amount]) => {
      const lote = lotes.find(l => l.loteId === loteId);
      return { loteId, name: lote?.name ?? loteId, amount, perHa: lote?.hectareas && lote.hectareas > 0 ? amount / lote.hectareas : null };
    });
  const excludedCurrency = Math.max(general?.excludedOtherBaseCurrency ?? 0, cashflow?.generalExpensesExcludedOtherBaseCurrency ?? 0);
  const indirectColumns: DataTableColumn<(typeof indirectRows)[number]>[] = [
    { key: 'name', header: c.colLote, width: 1.4 },
    { key: 'amount', header: c.colIndirect, align: 'right', render: r => fmtBase(r.amount) },
    { key: 'perHa', header: c.colIndirectPerHa, align: 'right', render: r => r.perHa == null ? '—' : fmtBase(r.perHa) },
  ];

  const budgetColumns: DataTableColumn<BudgetBreakdownRow>[] = [
    { key: 'category', header: c.colCategory, width: 1.4 },
    { key: 'planned', header: c.colPlanned, align: 'right', render: r => fmt(r.planned) },
    { key: 'executed', header: c.colExecuted, align: 'right', render: r => fmt(r.executed) },
    {
      key: 'variance', header: c.colVariance, align: 'right',
      render: r => (
        <span style={{ color: r.variance >= 0 ? tokens.colorPaletteGreenForeground1 : tokens.colorStatusDangerForeground1 }}>
          {r.variance >= 0 ? '+' : ''}{fmt(r.variance)}
        </span>
      ),
    },
    { key: 'pct', header: c.colPct, align: 'right', render: r => r.pct == null ? '—' : `${r.pct.toFixed(1)}%` },
  ];

  return (
    <div className={s.root}>
      <PageHeader title={c.title} subtitle={c.subtitle} />

      {(plLoading || budgetLoading) && <Spinner size="small" label={c.loading} />}

      {pl && budget && (
        <div className={s.kpiRow}>
          <KpiCard label={c.kpiPlanned} value={fmt(budget.totalPlanned)} />
          <KpiCard label={c.kpiExecuted} value={fmt(budget.totalExecuted)} />
          <KpiCard label={c.kpiExecPct} value={execPct == null ? '—' : `${execPct.toFixed(1)}%`} />
          {general && <KpiCard label={c.kpiGeneralExpenses} value={fmtBase(general.total)} />}
          <KpiCard label={c.kpiRevenue} value={fmt(pl.totalRevenue)} />
          <KpiCard label={c.kpiMargin} value={fmt(pl.grossMargin)} tone={pl.grossMargin < 0 ? 'danger' : 'default'} />
          <KpiCard label={c.kpiMarginPct} value={pl.marginPct == null ? '—' : `${pl.marginPct.toFixed(1)}%`} />
        </div>
      )}

      <div className={s.splitRow}>
        <div className={s.splitCol}>
          <ChartCard title={c.compositionTitle} isEmpty={donutData.length === 0} emptyMessage={c.noData}>
            <DonutChart data={donutData} centerValue={fmt(categoryTotal)} centerLabel={c.compositionCenterLabel} />
          </ChartCard>
        </div>
        <div className={s.splitCol}>
          <ChartCard title={c.cashflowTitle} isLoading={cashflowLoading} isEmpty={cashflowData.length < 2} emptyMessage={c.noData}>
            <BarLineChart data={cashflowData} />
          </ChartCard>
        </div>
      </div>

      <div className={s.section}>
        <span className={s.sectionTitle}>{c.budgetTitle}</span>
        <DataTable
          columns={budgetColumns}
          rows={(budget?.breakdown ?? []).map(r => ({ ...r, category: categoryName(r.category) }))}
          rowKey={r => r.category}
          isLoading={budgetLoading}
          emptyMessage={c.noData}
          totals={budget ? { planned: fmt(budget.totalPlanned), executed: fmt(budget.totalExecuted) } : undefined}
        />
      </div>

      {excludedCurrency > 0 && <MessageBar intent="warning"><MessageBarBody>{c.excludedCurrencyNote} {excludedCurrency}</MessageBarBody></MessageBar>}

      {indirectRows.length > 0 && (
        <div className={s.section}>
          <span className={s.sectionTitle}>{c.indirectsTitle}</span>
          <DataTable columns={indirectColumns} rows={indirectRows} rowKey={r => r.loteId} emptyMessage={c.noData} />
        </div>
      )}

      <div className={s.section}>
        <span className={s.sectionTitle}>{c.receivablesTitle}</span>
        {agingLoading && <Spinner size="small" label={c.loading} />}
        {aging && aging.currencies.length === 0 && (
          <div className={s.kpiRow}>
            <KpiCard label={c.agingCurrent} value={fmt(0)} />
            {AGING_BUCKETS.map(b => (
              <KpiCard key={b} label={`${c.agingBucketPrefix} ${b}`} value={fmt(0)} />
            ))}
            <KpiCard label={c.agingTotal} value={fmt(0)} />
          </div>
        )}
        {aging && aging.currencies.map(cur => {
          const bucket = aging.byCurrency[cur];
          return (
            <div key={cur} className={s.kpiRow}>
              <KpiCard label={`${cur} · ${c.agingCurrent}`} value={formatCurrency(bucket.current, cur)} />
              {AGING_BUCKETS.map(b => (
                <KpiCard key={b} label={`${cur} · ${c.agingBucketPrefix} ${b}`} value={formatCurrency(bucket.buckets[b], cur)} accent={b === '90+' && bucket.buckets[b] > 0} />
              ))}
              <KpiCard label={`${cur} · ${c.agingTotal}`} value={formatCurrency(bucket.totalOutstanding, cur)} />
            </div>
          );
        })}
      </div>
    </div>
  );
}
